import { useState } from "react";
import { X, MessageSquare, Star, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { submitFeedback } from "@/lib/firestoreService";
import { toast } from "sonner";

export function FeedbackModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please enter a feedback message.");
      return;
    }

    setSubmitting(true);
    try {
      const feedbackData = {
        message: message.trim(),
      };

      if (rating > 0) {
        feedbackData.rating = rating;
      }

      if (user) {
        feedbackData.user_id = user.id || user.uid || "unknown";
        feedbackData.user_email = user.email || user.phone_e164 || "unknown";
      } else {
        feedbackData.user_id = "anonymous";
        feedbackData.user_email = "anonymous";
      }

      await submitFeedback(feedbackData);
      setSubmitted(true);
      toast.success("Feedback sent to administrators!");
    } catch (err) {
      console.error("Error submitting feedback:", err);
      toast.error(err?.message || "Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setRating(0);
    setMessage("");
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      {/* Background overlay click */}
      <div className="absolute inset-0" onClick={onClose} />
      
      <div 
        id="feedback-modal-card"
        className="relative w-full max-w-md rounded-3xl bg-white dark:bg-[#132331] border border-slate-100 dark:border-white/5 p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <button 
          id="feedback-close-btn"
          onClick={onClose} 
          className="absolute right-4 top-4 rounded-full bg-slate-50 hover:bg-slate-100 dark:bg-[#1C3345] dark:hover:bg-[#254259] p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>

        {!submitted ? (
          <form id="feedback-form" onSubmit={handleSubmit} className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#0A3D5F]/10 dark:bg-white/5 text-[#0A3D5F] dark:text-[#EBF3F8]">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-bold text-[#0A3D5F] dark:text-[#EBF3F8]">
                  Leave Feedback
                </h3>
                <p className="text-xs text-slate-500 dark:text-[#7CA1B5]">
                  Help us improve your clinical workflow.
                </p>
              </div>
            </div>

            {/* Star Rating Section */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-400 dark:text-[#7CA1B5] uppercase tracking-wider">
                Overall Experience (Optional)
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95 cursor-pointer"
                  >
                    <Star
                      className={`h-6 w-6 transition-colors ${
                        star <= (hoverRating || rating)
                          ? "fill-amber-400 text-amber-400"
                          : "text-slate-200 dark:text-slate-700 hover:text-slate-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Message Area */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-400 dark:text-[#7CA1B5] uppercase tracking-wider">
                Your Message
              </label>
              <textarea
                required
                maxLength={1000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What can we do better? Found a bug or have a preset suggestion?"
                rows={4}
                className="w-full text-sm rounded-2xl border border-slate-200 dark:border-white/10 bg-[#F8FAFC] dark:bg-[#1C3345] px-4 py-3 text-slate-800 dark:text-[#EBF3F8] placeholder:text-slate-400 dark:placeholder:text-[#EBF3F8]/25 outline-none focus:border-[#2AA7A0] dark:focus:border-[#36CDC5] focus:ring-1 focus:ring-[#2AA7A0] resize-none"
              />
              <div className="flex justify-end text-[10px] text-slate-400">
                {message.length}/1000 characters
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-2xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-[#7CA1B5] hover:bg-slate-50 dark:hover:bg-[#1C3345] font-semibold text-xs tracking-wider uppercase transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0A3D5F] hover:bg-[#082E49] disabled:opacity-50 text-white font-bold text-xs tracking-wider uppercase shadow-md transition-all active:scale-[0.98] cursor-pointer"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    <span>Send Feedback</span>
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center py-6 space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div>
              <h3 className="font-serif text-xl font-bold text-[#0A3D5F] dark:text-[#EBF3F8]">
                Thank You!
              </h3>
              <p className="text-sm text-slate-500 dark:text-[#7CA1B5] font-light mt-1.5 leading-relaxed">
                Your feedback has been successfully delivered to our development and administrative teams. We appreciate you taking the time to write.
              </p>
            </div>
            <button
              onClick={handleReset}
              className="mt-2 inline-flex h-10 px-6 items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-[#1C3345] dark:hover:bg-[#254259] text-slate-700 dark:text-[#EBF3F8] font-semibold text-xs tracking-wider uppercase transition-all"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
